'use client';
import StoreConfig from '@/config';
import useIsMobile from '@/hooks/use-is-mobile';
import { useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

import { useTypePolicies } from '@/common/hooks/useTypePolicies';
import { useSwitchCountry } from '@/hooks/use-switch-country';
import { CUSTOM_TYPES } from './currencySwitcher.gql';

const CountrySwitcher = () => {
  const { handleSwitchCountry } = useSwitchCountry();

  useTypePolicies(CUSTOM_TYPES);

  const availableCountries = StoreConfig.available_countries;
  const currentCountryCode = StoreConfig.current_country_code;

  const isMobile = useIsMobile();
  const [showCountryOptions, setShowCountryOptions] = useState(false);
  const [internalStoreCode, setInternalStoreCode] = useState(currentCountryCode);
  const triggerRef = useRef(null);
  const prevPropsRef = useRef({ currentCountryCode });

  if (prevPropsRef.current.currentCountryCode !== currentCountryCode) {
    prevPropsRef.current.currentCountryCode = currentCountryCode;
    setInternalStoreCode(currentCountryCode);
  }

  const currentCountry = useMemo(() => {
    return availableCountries.find(temp => temp.code === currentCountryCode);
  }, [availableCountries, currentCountryCode]);

  if (!availableCountries || availableCountries.length === 1) {
    return null;
  }

  const currencies = availableCountries.map((country, i, arr) => {
    if (country.hideInOptions) return null;

    const active =
      country.code === currentCountryCode || (currentCountry.hideInOptions && currentCountry.name === country.name);

    return (
      <li
        role='option'
        aria-selected={currentCountryCode}
        key={country.code}
        className='cursor-pointer hover_bg-[#F8F3EF]'
      >
        <button
          type='button'
          disabled={active}
          onClick={() => {
            handleSwitchCountry(country.code);
          }}
          className='flex w-full items-center justify-between px-[5px] py-2'
        >
          <span className='flex items-center gap-x-2.5'>
            <img
              src={country.icon}
              className='h-auto w-7'
              alt=''
            />
            <span className=' text-[15px] leading-[20px] text-[#403732]'>{country.name}</span>
          </span>
          {active ? (
            <svg
              xmlns='http://www.w3.org/2000/svg'
              width='20'
              height='20'
              fill='none'
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              className='icon_icon__ckKxp'
              viewBox='0 0 24 24'
            >
              <path d='M20 6 9 17l-5-5'></path>
            </svg>
          ) : null}
        </button>
      </li>
    );
  });

  const sameCountries = availableCountries.filter(x => x.name === currentCountry.name);

  const content = (
    <>
      <p className='mb-[5px] text-[15px] leading-[1.4] text-[#403732]'>Country</p>
      <ul
        className='mb-[10px]'
        role='listbox'
      >
        {currencies}
      </ul>
      <p className='mb-[5px] text-[15px] leading-[1.4] text-[#403732]'>Currency</p>
      <p className='mb-[10px] border border-[#E7DED5] px-[10px] text-[15px] leading-[40px]'>
        {currentCountry.currency}
      </p>

      <p className='mb-[5px] text-[15px] leading-[1.4] text-[#403732]'>Language</p>
      {sameCountries.length <= 1 ? (
        <p className=' border border-[#E7DED5] px-[10px] text-[15px] leading-[40px]'>{currentCountry.language}</p>
      ) : (
        <div className='relative'>
          <select
            className=' h-10 w-full appearance-none border border-[#E7DED5] px-2.5 text-[15px]'
            value={internalStoreCode}
            onChange={e => {
              const code = e.target.value;

              handleSwitchCountry(code);
              setInternalStoreCode(code);
            }}
          >
            {sameCountries.map(x => {
              return (
                <option
                  key={x.code}
                  value={x.code}
                >
                  {x.language}
                </option>
              );
            })}
          </select>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='18'
            height='11'
            fill='none'
            viewBox='0 0 18 11'
            className='absolute right-2.5 top-1/2 block -translate-y-1/2'
          >
            <g clipPath='url(#clip0_16104_23593)'>
              <path
                stroke='#80583F'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='1.5'
                d='M2.5 2 9 9l6.5-7'
              ></path>
            </g>
            <defs>
              <clipPath id='clip0_16104_23593'>
                <path
                  fill='#fff'
                  d='M0 11V0h18v11z'
                ></path>
              </clipPath>
            </defs>
          </svg>
        </div>
      )}
    </>
  );
  const trigger = (
    <button
      type='button'
      className='flex items-center lg_gap-x-2'
      aria-label={currentCountryCode}
      ref={triggerRef}
      aria-expanded={showCountryOptions}
    >
      <div className='flex h-[22px] w-[22px] items-center overflow-hidden rounded-full text-sm md_h-auto md_w-auto md_rounded-none'>
        <img
          alt=''
          className='h-auto w-[22px] lg_w-7'
          src={isMobile ? currentCountry.mobileIcon : currentCountry.icon}
        />
      </div>
      <svg
        className='hidden lg_block'
        xmlns='http://www.w3.org/2000/svg'
        width='10'
        height='5'
        fill='none'
        viewBox='0 0 10 5'
      >
        <path
          fill='#5E5047'
          d='M10 0H7.93L5 2.93 2.07 0H0l5 5z'
        />
      </svg>
    </button>
  );

  return (
    <div>
      {isMobile ? (
        <Dialog
          open={showCountryOptions}
          onOpenChange={setShowCountryOptions}
        >
          <DialogTrigger asChild>{trigger}</DialogTrigger>
          <DialogContent
            className='border-0 z-[200] block h-full w-full gap-y-0 bg-white px-[30px] pb-[30px] pt-[60px] outline-0'
            showCloseButton={false}
          >
            <DialogTitle className='hidden'>country options</DialogTitle>
            <button
              type='button'
              onClick={() => {
                setShowCountryOptions(false);
              }}
              className='absolute right-0 top-0 flex h-[46px] w-[46px] items-center justify-center'
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                fill='none'
                stroke='#5E5047'
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                viewBox='0 0 24 24'
              >
                <path d='M18 6 6 18M6 6l12 12'></path>
              </svg>
            </button>
            {content}
          </DialogContent>
        </Dialog>
      ) : (
        <Popover
          open={showCountryOptions}
          onOpenChange={setShowCountryOptions}
        >
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent
            align='start'
            sideOffset={16}
            alignOffset={-4}
            className='w-[300px] bg-white px-5 py-[25px] shadow-[0_0_4px_0_rgba(0,0,0,0.15)]'
          >
            {content}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default CountrySwitcher;
